import { stripCep } from '@/utils/masks';

const VIA_CEP_URL = 'https://viacep.com.br/ws';

export type CepAddress = {
  street: string;
  neighborhood: string;
  city: string;
  state: string;
  complement: string;
};

export type CepLookupError = 'not_found' | 'network';

export type CepLookupResult =
  | { ok: true; address: CepAddress }
  | { ok: false; error: CepLookupError };

type ViaCepResponse = {
  erro?: boolean | string;
  logradouro?: string;
  bairro?: string;
  localidade?: string;
  uf?: string;
  complemento?: string;
};

function asText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Consulta endereço público no ViaCEP.
 * CEP com ou sem máscara; só dispara fetch com 8 dígitos.
 * Retorna null quando o CEP está incompleto (sem chamada de rede).
 */
export async function lookupCep(
  cep: string,
  options?: { signal?: AbortSignal },
): Promise<CepLookupResult | null> {
  const digits = stripCep(cep);
  if (digits.length !== 8) {
    return null;
  }

  try {
    const response = await fetch(`${VIA_CEP_URL}/${digits}/json/`, {
      signal: options?.signal,
    });

    if (!response.ok) {
      return { ok: false, error: 'network' };
    }

    const data = (await response.json()) as ViaCepResponse;
    if (data.erro) {
      return { ok: false, error: 'not_found' };
    }

    return {
      ok: true,
      address: {
        street: asText(data.logradouro),
        neighborhood: asText(data.bairro),
        city: asText(data.localidade),
        state: asText(data.uf),
        complement: asText(data.complemento),
      },
    };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    return { ok: false, error: 'network' };
  }
}
